import {FC, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import clsx from 'clsx';
import Checkboxes from 'components/Form/Checkboxes';
import InputHidden from 'components/Form/InputHidden';
import InputIncremental from 'components/Form/InputIncremental';
import Radio from 'components/Form/Radio';
import SubmitButton from 'components/Form/SubmitButton';
import I18nImage from 'components/I18nImage';
import Stacked from 'components/Layout/Stacked';
import {FormFields} from 'types/utils';
import {toCurrency} from 'utils/number';
import organizationEN from '../assets/images/organization_en.png';
import organizationJA from '../assets/images/organization_ja.png';
import BillingChangeSubscriptionHeader from '../Header';
import parseChangeSubscriptionFormData from '../parse-change-subscription-form-data';
import PriceCalculation from '../PriceCalculation';
import TabNavigation from '../TabNavigation';
import {
    BillingPlanIntegrationsPriceList,
    BillingPlanOption,
    BillingPlanOptionsPriceList,
    BillingScreenName,
    ParsedSearchParams,
    Term,
    TermOptions,
} from '../types';
import useGetOrganizationPrices from './services';

type PageOrganizationPublicProps = {
    integrationsPrices: BillingPlanIntegrationsPriceList;
    optionPrices: BillingPlanOptionsPriceList;
    termOptions: TermOptions;
};

type PageOrganizationPrivateProps = {
    isAuthenticated: boolean;
    onSuccess: (url: string) => void;
    onTabChange: (tag: BillingScreenName) => void;
};

type PageOrganizationFormData = FormFields<
    {
        activeClients: number;
        billingFrequency: Term;
        plan_code: 'organization_2';
        teamMembers: number;
    } & {[K in BillingPlanOption]: boolean}
>;

const PageOrganization: FC<
    PageOrganizationPublicProps & PageOrganizationPrivateProps
> = ({
    integrationsPrices,
    isAuthenticated,
    onSuccess,
    onTabChange,
    optionPrices,
    termOptions,
}) => {
    const methods = useForm<PageOrganizationFormData>({
        defaultValues: {
            activeClients: 10,
            billingFrequency: 'monthly',
            teamMembers: 1,
        },
        shouldUnregister: true,
    });

    const {getValues, handleSubmit, watch} = methods;

    const [searchParams, setSearchParams] = useState<ParsedSearchParams>(() =>
        parseChangeSubscriptionFormData(getValues()),
    );
    const {data, isError, isLoading, isSuccess} = useGetOrganizationPrices({
        onError: () => {},
        searchParams,
    });

    watch((formData) => {
        setSearchParams(parseChangeSubscriptionFormData(formData));
    });

    const {i18n, t} = useTranslation('Billing.PlanChange');

    const termOptionRadio = termOptions.map((option) => ({
        disabled: option.disabled,
        label: (
            <span>
                <Trans
                    components={[
                        <span
                            key={0}
                            className={clsx(
                                'rounded px-2 py-0.5 text-xs font-semibold text-white',
                                option.disabled ? 'bg-grey-400' : 'bg-blue-400',
                            )}
                        />,
                    ]}
                    i18nKey={`billingTerms.${option.i18NKey}`}
                    ns="Billing.PlanChange"
                />
            </span>
        ),
        value: option.value,
    }));

    const hasTermHelpText = termOptions.some((option) => option.disabled);

    const additionlOptions = optionPrices.map((option) => ({
        label: (
            <div>
                <span>{t(`checkboxes.options.${option.name}`)}</span>
                {option.value && (
                    <span className="text-sm text-grey-500">
                        {' '}
                        {toCurrency({currency: 'JPY', value: option.value})}
                    </span>
                )}
            </div>
        ),
        name: option.name,
    }));

    const integrationsOptions = integrationsPrices.map(({name, value}) => ({
        label: (
            <div>
                <span>{t(`checkboxes.app.${name}`)}</span>
                {value && (
                    <span className="text-sm text-grey-500">
                        {' '}
                        {toCurrency({currency: 'JPY', value})}
                    </span>
                )}
            </div>
        ),
        name,
    }));

    const onSubmit = (formData: PageOrganizationFormData) => {
        onSuccess(
            `/billing/plan/checkout/?${new URLSearchParams(
                parseChangeSubscriptionFormData(formData),
            ).toString()}`,
        );
    };

    return (
        <div>
            <BillingChangeSubscriptionHeader
                isAuthenticated={isAuthenticated}
            />
            <TabNavigation activeTab="organization" onTabChange={onTabChange} />
            <div className="grid w-max max-w-full grid-cols-2 bg-green-10">
                <div className="flex w-full flex-col items-center p-8">
                    <I18nImage
                        className="max-h-48 max-w-48"
                        src={{
                            en: organizationEN,
                            ja: organizationJA,
                        }}
                    />
                    <div className="my-4 text-center">
                        {t('organization.description')}
                    </div>
                    <a
                        href={`https://www.makeleaps.com/${i18n.language}/pricing-detail`}
                    >
                        {t('shared.findOutMore')}
                    </a>
                </div>
                <div className="w-full">
                    <FormProvider {...methods}>
                        <form className="p-4" onSubmit={handleSubmit(onSubmit)}>
                            <InputHidden
                                name="plan_code"
                                value="organization_2"
                            />
                            <Stacked className="w-full">
                                <Stacked className="bg-green-50 p-4">
                                    <InputIncremental
                                        helpText={t(
                                            'organization.teamMembersHelpText',
                                        )}
                                        label={t('shared.teamMembers')}
                                        max={100_000_000}
                                        min={1}
                                        name="teamMembers"
                                        required={true}
                                        requiredType="asterisk"
                                    />
                                    <InputIncremental
                                        helpText={
                                            <Trans
                                                components={[
                                                    <a
                                                        key="0"
                                                        href={`https://www.makeleaps.com/${i18n.language}/pricing-detail`}
                                                    >
                                                        {' '}
                                                    </a>,
                                                ]}
                                                i18nKey="organization.clientsHelpText"
                                                ns="Billing.PlanChange"
                                            />
                                        }
                                        label={t('shared.activeClients')}
                                        max={100_000_000}
                                        min={10}
                                        name="activeClients"
                                        required={true}
                                        requiredType="asterisk"
                                        step={5}
                                    />
                                </Stacked>
                                <div>
                                    <div>
                                        <Trans
                                            components={[
                                                <a
                                                    key={0}
                                                    className="text-xs"
                                                    href="/"
                                                >
                                                    {' '}
                                                </a>,
                                            ]}
                                            i18nKey="shared.options"
                                            t={t}
                                        />
                                    </div>
                                    <Checkboxes options={additionlOptions} />
                                </div>
                                <div>
                                    <div>{t('shared.integrations')}</div>
                                    <Checkboxes options={integrationsOptions} />
                                </div>
                                <Radio
                                    helpText={
                                        hasTermHelpText
                                            ? t('billingTerms.helpText')
                                            : undefined
                                    }
                                    label={t('shared.billingFrequency')}
                                    name="billingFrequency"
                                    options={termOptionRadio}
                                    required={true}
                                    requiredType="asterisk"
                                />
                                <div className="grid rounded bg-green-200">
                                    <PriceCalculation
                                        isError={isError}
                                        isLoading={isLoading}
                                        isSuccess={isSuccess}
                                        value={data?.data.amount}
                                    />
                                    <SubmitButton disabled={isError}>
                                        {t('shared.subscribe')}
                                    </SubmitButton>
                                </div>
                            </Stacked>
                        </form>
                    </FormProvider>
                </div>
            </div>
            <div className="flex w-full items-center justify-center whitespace-pre border-y border-grey-100 py-4 text-sm">
                <Trans
                    components={[
                        <a key={0} href="/">
                            {' '}
                        </a>,
                        <a key={1} href="/">
                            {' '}
                        </a>,
                    ]}
                    i18nKey="shared.freeForLimitedTime"
                    t={t}
                />
            </div>
        </div>
    );
};

export default PageOrganization;
